import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { Loading } from "../../../components/Loading";
import { getDriversList } from "../../../actions/inventory/driver.action";
import {
  getVerificationReviews,
  updateVerificationDecision,
} from "../../../actions/verificationReview.action";
import { DRIVER_OPERATIONAL_STATUS } from "../../../constants/driverStatus";

const ITEMS_PER_PAGE = 10;

const statusColorMap = {
  pending: "yellow",
  under_review: "yellow",
  clarification_requested: "orange",
  approved: "green",
  rejected: "red",
  all: "gray",
};

const accountStatusColorMap = {
  active: "green",
  inactive: "gray",
  suspended: "orange",
  blacklisted: "red",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLabel = (value = "") =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const deriveReviewStatus = (row) => {
  if (row.reviewStatus) return row.reviewStatus;
  if (row.isVerified || row.verificationStatus === "approved") return "approved";
  if (row.verificationStatus === "rejected") return "rejected";
  return "under_review";
};

const getTabStatus = (status) =>
  ["under_review", "clarification_requested"].includes(status) ? "pending" : status;

const getDocStatus = (documentation) => {
  if (!documentation) return "Missing";
  const dl = documentation?.drivingLicensePhotos || {};
  const aadhaar = documentation?.aadhaarCardPhotos || documentation?.adhaarCardPhotos || {};
  const pan = documentation?.panCardPhotos || {};
  const requiredFiles = [dl.front, dl.back, aadhaar.front, aadhaar.back, pan.front];
  return requiredFiles.every((value) => typeof value === "string" && value.trim()) ? "Complete" : "Incomplete";
};

const DriverApprovals = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("pending");
  const debounceTimer = useRef(null);


  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const response = await getDriversList(currentPage, ITEMS_PER_PAGE, query);
      let reviews = [];

      try {
        const reviewResponse = await getVerificationReviews({
          entityType: "driver",
          limit: 200,
          search: query,
        });
        reviews = reviewResponse?.data || [];
      } catch (error) {
        console.error(error.message);
      }

      const reviewByEntityId = new Map(
        reviews
          .filter((review) => review.entityId)
          .map((review) => [String(review.entityId), review]),
      );

      const normalized = (response?.data || []).map((item) => {
        const documentation = item?.documentation || {};
        const review = reviewByEntityId.get(String(item._id));
        return {
          id: item._id,
          entityId: item._id,
          reviewId: review?._id || null,
          uniqueDriverId: item?.uniqueDriverId || "—",
          name: item?.name || "—",
          mobileNumber: item?.contactDetails?.mobileNumber || "—",
          whatsappNumber: item?.contactDetails?.whatsappNumber || "—",
          email: item?.contactDetails?.email || "—",
          city: item?.contactDetails?.city || "—",
          state: item?.contactDetails?.state || "—",
          submittedAt: item?.createdAt || item?.updatedAt || null,
          documentation,
          documentsStatus: item?.documentationAvailable ? "Complete" : getDocStatus(documentation),
          isVerified: Boolean(item?.isVerified),
          verificationStatus: item?.verificationStatus || null,
          operationalStatus: item?.aditionalDetails?.operationalStatus || "offline",
          accountStatus: item?.aditionalDetails?.accountStatus || "active",
          reviewStatus: review?.status || "",
          clarificationMessages: review?.clarificationMessages || [],
          raw: item,
        };
      });

      const existingEntityIds = new Set(normalized.map((item) => String(item.entityId)));
      const reviewOnlyRows = reviews
        .filter((review) => !review.entityId || !existingEntityIds.has(String(review.entityId)))
        .map((review) => ({
          id: review._id,
          entityId: review.entityId || null,
          reviewId: review._id,
          reviewOnly: true,
          uniqueDriverId: review.displayCode || "—",
          name: review.displayName || review.title || "—",
          mobileNumber: review.mobileNumber || "—",
          whatsappNumber: "—",
          email: "—",
          city: "—",
          state: "—",
          submittedAt: review.submittedAt || review.createdAt || null,
          documentation: review.documents || {},
          documentsStatus: Object.keys(review.documents || {}).length ? "Complete" : "Incomplete",
          isVerified: review.status === "approved",
          operationalStatus: DRIVER_OPERATIONAL_STATUS.OFFLINE,
          accountStatus: review.status === "rejected" ? "inactive" : "active",
          reviewStatus: review.status,
          clarificationMessages: review.clarificationMessages || [],
          raw: review,
        }));

      setData([...reviewOnlyRows, ...normalized]);
      setTotalPages(Math.max(1, Number(response?.totalPages) || 1));
    } catch (error) {
      toast({
        title: "Failed to fetch driver approvals",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setData([]);
      setTotalPages(1);
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [currentPage, query, toast]);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredData = useMemo(() => {
    if (statusFilter === "all") return data;
    return data.filter((row) => getTabStatus(deriveReviewStatus(row)) === statusFilter);
  }, [data, statusFilter]);

  const statusTabs = ["pending", "approved", "rejected", "all"];

  const openDetails = (driver) => {
    navigate(`/verification/driver-approvals/${driver.id}`, {
      state: { row: driver },
    });
  };

  const updateLocalDriver = (driverId, updater) => {
    setData((prev) =>
      prev.map((row) =>
        row.id === driverId ? { ...row, ...updater(row) } : row,
      ),
    );
  };

  const handleApprove = async (driver) => {
    try {
      // Entity-level decision: the account becomes Active only via the review rule
      const response = await updateVerificationDecision({
        entityType: "driver",
        entityId: driver.entityId || driver.id,
        reviewId: driver.reviewId,
        decision: "approve",
        row: driver,
      });
      updateLocalDriver(driver.id, () => ({
        isVerified: true,
        accountStatus: "active",
        reviewStatus: "approved",
        reviewId: response?.data?._id || driver.reviewId,
      }));
      toast({
        title: "Driver approved",
        description: "The approval is saved for the app verification status.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to approve driver",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) return <Loading />;

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "#", title: "Driver Approvals", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
          <Box>
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              Driver Approvals
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Live driver records from the inventory backend
            </Text>
          </Box>

          <Box position="relative">
            <Input
              placeholder="Search by name, mobile, or driver ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              pl={10}
              borderRadius="lg"
              w={{ base: "100%", md: "320px" }}
              bg="gray.50"
            />
            <Box position="absolute" left={3} top="50%" transform="translateY(-50%)">
              <FiSearch color="gray" />
            </Box>
          </Box>
        </Flex>

        <Tabs
          variant="soft-rounded"
          colorScheme="blue"
          mb={4}
          index={statusTabs.indexOf(statusFilter)}
          onChange={(index) => setStatusFilter(statusTabs[index] || "pending")}
        >
          <TabList>
            <Tab>Pending</Tab>
            <Tab>Approved</Tab>
            <Tab>Rejected</Tab>
            <Tab>All</Tab>
          </TabList>
        </Tabs>

        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Driver ID</Th>
                <Th>Name</Th>
                <Th>Mobile Number</Th>
                <Th>City / State</Th>
                <Th>Submitted At</Th>
                <Th>Documents</Th>
                <Th>Verification</Th>
                <Th>Account</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fetching ? (
                <Tr>
                  <Td colSpan={9} py={8} textAlign="center">
                    <Loading />
                  </Td>
                </Tr>
              ) : filteredData.length === 0 ? (
                <Tr>
                  <Td colSpan={9} py={8} textAlign="center">
                    <Text color="gray.500">No driver approvals found</Text>
                  </Td>
                </Tr>
              ) : (
                filteredData.map((row) => {
                  const reviewStatus = deriveReviewStatus(row);

                  return (
                    <Tr key={row.id}  _hover={{ bg: "gray.50" }}>
                      <Td onClick={() => openDetails(row)} fontWeight="medium">{row.uniqueDriverId}</Td>
                      <Td onClick={() => openDetails(row)}>{row.name}</Td>
                      <Td onClick={() => openDetails(row)}>{row.mobileNumber}</Td>
                      <Td onClick={() => openDetails(row)}>{`${row.city} / ${row.state}`}</Td>
                      <Td onClick={() => openDetails(row)}>{formatDate(row.submittedAt)}</Td>
                      <Td onClick={() => openDetails(row)}>
                        <Badge colorScheme={row.documentsStatus === "Complete" ? "green" : "orange"}>
                          {row.documentsStatus}
                        </Badge>
                      </Td>
                      <Td onClick={() => openDetails(row)}>
                        <Badge colorScheme={statusColorMap[reviewStatus] || "gray"}>
                          {formatLabel(reviewStatus)}
                        </Badge>
                      </Td>
                      <Td onClick={() => openDetails(row)}>
                        <Badge colorScheme={accountStatusColorMap[row.accountStatus] || "gray"}>
                          {formatLabel(row.accountStatus)}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem onClick={() => openDetails(row)}>View Details</MenuItem>
                            {reviewStatus !== "approved" && (
                              <MenuItem color="green.500" onClick={() => handleApprove(row)}>
                                Approve
                              </MenuItem>
                            )}
                            {reviewStatus !== "rejected" && (
                              <MenuItem color="red.500" onClick={() => openDetails(row)}>
                                Reject
                              </MenuItem>
                            )}
                            <MenuItem color="orange.500" onClick={() => openDetails(row)}>
                              Request Clarification
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={3}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Rows per page:
            </Text>
            <Select size="sm" value={ITEMS_PER_PAGE} w="74px" isDisabled>
              <option value={10}>10</option>
            </Select>
          </Flex>

          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              icon={<GoChevronLeft />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1 || fetching}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              aria-label="Previous page"
            />
            <IconButton
              icon={<GoChevronRight />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPages || fetching}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              aria-label="Next page"
            />
          </Flex>
        </Flex>
      </Box>

    </Stack>
  );
};

export default DriverApprovals;
